import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Eye, EyeOff } from "lucide-react";
import { generateLegalDoc, generateLegalDocPdf, today, type DocSection } from "./legalDocUtils";
import { offerDateProblems, salaryProblem } from "./legalValidation";
import { buildOfferSections, offerTitle } from "./offerLetterBuild";
import { LegalDocPreview } from "./LegalDocPreview";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates a real, structured offer letter — either an internship offer or a permanent-position offer, with the sections that actually differ between the two handled correctly: duration and stipend/unpaid status for an internship; salary, pay period, and benefits for a permanent role. Covers reporting structure, compensation, equipment and confidentiality, at-will status, contingencies, governing law, and a signature/acceptance block — not just a short generic letter.`,
  how: `Choose Internship or Permanent Position first — the rest of the form adjusts to match. Fill in the company, candidate, and role details; upload your company logo and (optionally) a signature image if you have one. Preview before downloading, then get it as a Word or PDF file.`,
  tip: `Every name, date, number, and the logo itself are placeholders until you fill them in — nothing here is specific to any one company. Keep the letter's language consistent with your actual employment contract or handbook; inconsistent terms between the two documents can create confusion or disputes later. This isn't legal advice — for a role with real regulatory complexity (equity, multi-state remote work, visa sponsorship), have an employment attorney review the final letter.`,
};

type PositionType = "internship" | "permanent";
type DurationUnit = "weeks" | "months";
type PayPeriod = "year" | "month";
type EquipmentType = "own" | "company";

async function fileToBytes(file: File | null): Promise<Uint8Array | undefined> {
  if (!file) return undefined;
  return new Uint8Array(await file.arrayBuffer());
}
function imgType(file: File | null): "png" | "jpg" | undefined {
  if (!file) return undefined;
  return file.type === "image/png" ? "png" : "jpg";
}
export default function EmploymentOfferLetterGenerator() {
  const [positionType, setPositionType] = useState<PositionType>("permanent");

  // Company
  const [companyName, setCompanyName] = useState("");
  const [companyTagline, setCompanyTagline] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [offerId, setOfferId] = useState("");

  // Candidate
  const [candidateName, setCandidateName] = useState("");
  const [candidateLocation, setCandidateLocation] = useState("");

  // Role
  const [jobTitle, setJobTitle] = useState("");
  const [employmentType, setEmploymentType] = useState("Full-time");
  const [managerName, setManagerName] = useState("");
  const [managerTitle, setManagerTitle] = useState("");
  const [mentorName, setMentorName] = useState("");
  const [mentorNote, setMentorNote] = useState("who will provide day-to-day guidance on role expectations and task delivery");
  const [startDate, setStartDate] = useState("");
  const [durationNumber, setDurationNumber] = useState("3");
  const [durationUnit, setDurationUnit] = useState<DurationUnit>("months");

  // Compensation
  const [isPaidInternship, setIsPaidInternship] = useState(false);
  const [salary, setSalary] = useState("");
  const [salaryPeriod, setSalaryPeriod] = useState<PayPeriod>("year");
  const [benefits, setBenefits] = useState("Health insurance, paid time off, and participation in company benefit plans as described in the employee handbook.");

  // Internship-only extras
  const [learningPoints, setLearningPoints] = useState(
    "Engage in high-impact technical and business activities that contribute to real-world product development\nReceive guidance from experienced professionals to develop both technical skills and business acumen\nEnjoy autonomy to explore tools, technologies, and approaches that best support the delivery of assigned work\nReceive a Certificate of Experience upon successful completion of the internship"
  );

  // Equipment & contingencies
  const [equipmentType, setEquipmentType] = useState<EquipmentType>("company");
  const [contingencyCriminalCheck, setContingencyCriminalCheck] = useState(true);
  const [contingencyBackgroundCheck, setContingencyBackgroundCheck] = useState(false);
  const [contingencyReferenceCheck, setContingencyReferenceCheck] = useState(false);
  const [contingencyWorkEligibility, setContingencyWorkEligibility] = useState(true);

  const [termination, setTermination] = useState<"at-will" | "notice">("at-will");
  const [noticeDays, setNoticeDays] = useState("30");
  const [state, setState] = useState("");
  const [responseDeadline, setResponseDeadline] = useState("");

  // Signature
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signatureFile, setSignatureFile] = useState<File | null>(null);

  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);

  const isInternship = positionType === "internship";
  const salaryErr = !isInternship || isPaidInternship ? salaryProblem(salary, isInternship ? "Stipend" : "Salary") : null;
  const dateErr = offerDateProblems(startDate, responseDeadline);
  const durationErr = isInternship && (!(Number(durationNumber) >= 1) || !Number.isInteger(Number(durationNumber))) ? "Enter a whole number of 1 or more." : null;
  const noticeErr = termination === "notice" && (!(Number(noticeDays) >= 1) || !Number.isInteger(Number(noticeDays))) ? "Enter a whole number of 1 or more." : null;
  const hasFieldProblems = !!(salaryErr || dateErr.start || dateErr.deadline || durationErr || noticeErr);
  const missingRequired = hasFieldProblems || !companyName.trim() || !candidateName.trim() || !jobTitle.trim() || !startDate.trim() || (!isInternship && !salary.trim());

  const title = offerTitle({ positionType });
  const docTypeLabel = isInternship ? "Internship Offer Letter" : "Offer of Employment";

  const buildSections = (resolvedSignatureImage?: { imageBytes: Uint8Array; imageType: "png" | "jpg" }): DocSection[] => {
    const sections = buildOfferSections({
      positionType, companyName, companyTagline, candidateName, jobTitle, employmentType, managerName, managerTitle, mentorName, mentorNote,
      startDate, durationNumber, durationUnit, isPaidInternship, salary, salaryPeriod, benefits, learningPoints, equipmentType,
      contingencyCriminalCheck, contingencyBackgroundCheck, contingencyReferenceCheck, contingencyWorkEligibility,
      termination, noticeDays, state, responseDeadline, signerName, signerTitle, placeholders: true,
    }, !!resolvedSignatureImage);
    // A signature image belongs right after "Sincerely," and before the acceptance block.
    return resolvedSignatureImage ? sections.map((s) => (s.paragraphs.includes("Sincerely,") ? { ...s, signatureImage: resolvedSignatureImage } : s)) : sections;
  };

  const subtitle = [offerId ? `Offer ID: ${offerId}` : "", `Date: ${today()}`].filter(Boolean).join("   |   ");

  const buildOptions = async () => {
    const logoBytes = await fileToBytes(logoFile);
    return {
      letterhead: companyName
        ? {
            logoBytes,
            logoType: imgType(logoFile),
            companyLine1: companyName,
            companyLine2: [companyTagline, companyAddress].filter(Boolean).join(" \u2022 ") || undefined,
          }
        : undefined,
      footerText: `${docTypeLabel} \u2013 Confidential`,
    };
  };

  const resolveSignatureImage = async () => {
    const sigBytes = await fileToBytes(signatureFile);
    return sigBytes && signatureFile ? { imageBytes: sigBytes, imageType: imgType(signatureFile)! } : undefined;
  };

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating at all, while handleDownload (the real value-delivering
  // action) had no usage-limit check of any kind. Same fix as the
  // other 7 legal-doc generators: gate the one shared download
  // handler directly with logUsageNow() before it does any real work.
  const { logUsageNow } = useLogUsageOnAction();

  const handleDownload = async (format: "docx" | "pdf") => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    setGenerating(true);
    try {
      const options = await buildOptions();
      const signatureImage = await resolveSignatureImage();
      const filename = `${(candidateName || "offer-letter").replace(/[^\w-]+/g, "_")}-${isInternship ? "internship" : "offer"}.${format}`;
      if (format === "docx") await generateLegalDoc(title, buildSections(signatureImage), filename, subtitle, options);
      else await generateLegalDocPdf(title, buildSections(signatureImage), filename, subtitle, options);
    } finally {
      setGenerating(false);
    }
  };


  return (
    <ToolContainer>
        <div className="max-w-lg mx-auto space-y-4">
          <div>
            <Label>Position Type</Label>
            <Select value={positionType} onValueChange={(v) => { setPositionType(v as PositionType); setShowPreview(false); }}>
              <SelectTrigger aria-label="Position type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="permanent">Permanent Position</SelectItem>
                <SelectItem value="internship">Internship</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Company</p>
            <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
              <div><Label>Company Name *</Label><Input aria-label="Company Name" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g. Acme Inc." /></div>
              <div><Label>Offer ID (optional)</Label><Input aria-label="Offer ID (optional)" value={offerId} onChange={e => setOfferId(e.target.value)} placeholder="e.g. 002" /></div>
            </div>
            <div><Label>Tagline (optional)</Label><Input aria-label="Tagline (optional)" value={companyTagline} onChange={e => setCompanyTagline(e.target.value)} placeholder="e.g. A Property of Acme Holdings LLC" /></div>
            <div><Label>Address / Location (optional)</Label><Input aria-label="Address / Location (optional)" value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} placeholder="e.g. North Carolina, USA" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Company Logo (optional)</Label>
              <Input aria-label="Company Logo (optional)" type="file" accept="image/png,image/jpeg" onChange={e => setLogoFile(e.target.files?.[0] ?? null)} />
            </div>
          </Card>

          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Candidate</p>
            <div><Label>Candidate Name *</Label><Input aria-label="Candidate Name" value={candidateName} onChange={e => setCandidateName(e.target.value)} /></div>
            <div><Label>Candidate Location (optional)</Label><Input aria-label="Candidate Location (optional)" value={candidateLocation} onChange={e => setCandidateLocation(e.target.value)} placeholder="e.g. Raleigh, USA" /></div>
          </Card>

          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Role</p>
            <div><Label>Job Title *</Label><Input aria-label="Job Title" value={jobTitle} onChange={e => setJobTitle(e.target.value)} /></div>
            <div>
              <Label>Basis</Label>
              <Select value={employmentType} onValueChange={v => { setEmploymentType(v); if (v === "Contract" || v === "Temporary") setTermination("notice"); }}>
                <SelectTrigger aria-label="Basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full-time">Full-time</SelectItem>
                  <SelectItem value="Part-time">Part-time</SelectItem>
                  {!isInternship && <SelectItem value="Contract">Contract</SelectItem>}
                  {!isInternship && <SelectItem value="Temporary">Temporary</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
              <div>
                <Label>Termination terms</Label>
                <Select value={termination} onValueChange={v => setTermination(v as "at-will" | "notice")}>
                  <SelectTrigger aria-label="Termination terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="at-will">At-will (United States)</SelectItem>
                    <SelectItem value="notice">Notice period (contract roles, outside the US)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {termination === "notice" && <div><Label htmlFor="ol-notice">Notice (days)</Label><Input id="ol-notice" type="number" min="1" aria-invalid={!!noticeErr} value={noticeDays} onChange={e => setNoticeDays(e.target.value)} />{noticeErr && <p role="alert" className="text-xs text-destructive mt-1">{noticeErr}</p>}</div>}
            </div>
            <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
              <div><Label>Manager Name (optional)</Label><Input aria-label="Manager Name (optional)" value={managerName} onChange={e => setManagerName(e.target.value)} /></div>
              <div><Label>Manager Title (optional)</Label><Input aria-label="Manager Title (optional)" value={managerTitle} onChange={e => setManagerTitle(e.target.value)} placeholder="e.g. VP Engineering" /></div>
            </div>
            {isInternship && (
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Mentor Name (optional)</Label><Input aria-label="Mentor Name (optional)" value={mentorName} onChange={e => setMentorName(e.target.value)} /></div>
                <div><Label className="text-xs text-muted-foreground">Mentor's Role Note</Label><Input aria-label="Mentor's Role Note" value={mentorNote} onChange={e => setMentorNote(e.target.value)} /></div>
              </div>
            )}
            <div><Label htmlFor="ol-start">Start Date *</Label><Input id="ol-start" type="date" aria-invalid={!!dateErr.start} value={startDate} onChange={e => setStartDate(e.target.value)} />{dateErr.start && <p role="alert" className="text-xs text-destructive mt-1">{dateErr.start}</p>}</div>
            {isInternship && (
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
                <div><Label htmlFor="ol-duration">Duration</Label><Input id="ol-duration" type="number" min="1" aria-invalid={!!durationErr} value={durationNumber} onChange={e => setDurationNumber(e.target.value)} />{durationErr && <p role="alert" className="text-xs text-destructive mt-1">{durationErr}</p>}</div>
                <div>
                  <Label>Unit</Label>
                  <Select value={durationUnit} onValueChange={(v) => setDurationUnit(v as DurationUnit)}>
                    <SelectTrigger aria-label="Duration unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weeks">Weeks</SelectItem>
                      <SelectItem value="months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Compensation</p>
            {isInternship && (
              <div className="flex items-center gap-2">
                <Checkbox checked={isPaidInternship} onCheckedChange={() => setIsPaidInternship(v => !v)} id="paid-internship" />
                <Label htmlFor="paid-internship" className="font-normal cursor-pointer">This is a paid internship (stipend)</Label>
              </div>
            )}
            {(!isInternship || isPaidInternship) && (
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
                <div><Label htmlFor="ol-salary">{isInternship ? "Stipend ($)" : "Salary ($)"} *</Label><Input id="ol-salary" type="number" inputMode="decimal" min={0} aria-invalid={!!salaryErr} value={salary} onChange={e => setSalary(e.target.value)} />{salaryErr && <p role="alert" className="text-xs text-destructive mt-1">{salaryErr}</p>}</div>
                <div>
                  <Label>Per</Label>
                  <Select value={salaryPeriod} onValueChange={(v) => setSalaryPeriod(v as PayPeriod)}>
                    <SelectTrigger aria-label="Pay period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="year">Year</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {isInternship ? (
              <div>
                <Label className="text-xs text-muted-foreground">What the intern will gain (one per line)</Label>
                <Textarea aria-label="What the intern will gain (one per line)" value={learningPoints} onChange={e => setLearningPoints(e.target.value)} className="min-h-[100px] font-mono text-xs" />
              </div>
            ) : (
              <div><Label>Benefits Summary</Label><Textarea aria-label="Benefits Summary" value={benefits} onChange={e => setBenefits(e.target.value)} className="min-h-[70px]" /></div>
            )}
          </Card>

          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Equipment &amp; Contingencies</p>
            <div>
              <Label>Equipment</Label>
              <Select value={equipmentType} onValueChange={(v) => setEquipmentType(v as EquipmentType)}>
                <SelectTrigger aria-label="Equipment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company provides equipment</SelectItem>
                  <SelectItem value="own">Uses their own device</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Checkbox checked={contingencyCriminalCheck} onCheckedChange={() => setContingencyCriminalCheck(v => !v)} id="c1" /><Label htmlFor="c1" className="font-normal cursor-pointer">Criminal record self-certification</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={contingencyBackgroundCheck} onCheckedChange={() => setContingencyBackgroundCheck(v => !v)} id="c2" /><Label htmlFor="c2" className="font-normal cursor-pointer">Background check</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={contingencyReferenceCheck} onCheckedChange={() => setContingencyReferenceCheck(v => !v)} id="c3" /><Label htmlFor="c3" className="font-normal cursor-pointer">Reference checks</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={contingencyWorkEligibility} onCheckedChange={() => setContingencyWorkEligibility(v => !v)} id="c4" /><Label htmlFor="c4" className="font-normal cursor-pointer">Work eligibility verification</Label></div>
            </div>
            <div><Label>Governing State (optional)</Label><Input aria-label="Governing State (optional)" value={state} onChange={e => setState(e.target.value)} placeholder="e.g. North Carolina" /></div>
            <div><Label htmlFor="ol-deadline">Response Deadline (optional)</Label><Input id="ol-deadline" type="date" aria-invalid={!!dateErr.deadline} value={responseDeadline} onChange={e => setResponseDeadline(e.target.value)} />{dateErr.deadline && <p role="alert" className="text-xs text-destructive mt-1">{dateErr.deadline}</p>}</div>
          </Card>

          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Signature</p>
            <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
              <div><Label>Signer Name</Label><Input aria-label="Signer Name" value={signerName} onChange={e => setSignerName(e.target.value)} /></div>
              <div><Label>Signer Title</Label><Input aria-label="Signer Title" value={signerTitle} onChange={e => setSignerTitle(e.target.value)} placeholder="e.g. CEO & Founder" /></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Signature Image (optional — leave blank for a signature line)</Label>
              <Input aria-label="Signature Image (optional — leave blank for a signature line)" type="file" accept="image/png,image/jpeg" onChange={e => setSignatureFile(e.target.files?.[0] ?? null)} />
            </div>
          </Card>

          {missingRequired && (
            <NoticeBox className="text-xs">Company, candidate name, job title, start date{!isInternship ? ", and salary" : ""} are required before you can preview or download.</NoticeBox>
          )}

          <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
          </Button>

          {showPreview && !missingRequired && (
            <LegalDocPreview title={title} subtitle={subtitle} sections={buildSections()} />
          )}

          <div className="flex gap-2">
            <Button onClick={() => handleDownload("docx")} disabled={missingRequired || generating} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Word
            </Button>
            <Button onClick={() => handleDownload("pdf")} disabled={missingRequired || generating} variant="secondary" className="flex-1 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This tool generates a general-purpose offer letter template. It isn't legal advice and may not account for the specific laws of your state, country, or situation — requirements for this type of offer letter can vary significantly by jurisdiction. Have any document reviewed by a qualified attorney before you sign it or rely on it, especially for anything involving significant money, property, or legal obligations." />
    </ToolContainer>
  );
}
